<template>
  <div class="chart-component" v-loading="chartLoading">
    <div ref="chartRef" class="chart-content" :style="chartHeight ? { height: `${chartHeight}px` } : {}"></div>
    <div
      class="legend-content"
      :class="chartTitle ? 'have-title' : ''"
      v-if="customLegend"
      v-show="legendData.length > 1"
    >
      <div class="legend-item single-ellipsis" v-for="(item, index) in legendData" :key="index">
        <div class="tip-content" :style="fetchBgStyle(index)"></div>
        <span class="legend-text">{{ item }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import chartComponent from './mixins/chart-component';
import { fetchAxisMaxAndInterval } from '@/lib/utils';

export default {
  mixins: [chartComponent],
  data() {
    return {
      legendData: []
    };
  },
  computed: {
    chartType() {
      return 'barChart';
    }
  },
  methods: {
    async initDataHandle(params) {
      const { originData } = params;
      const { rowsData, columnsData, legendsData, chartConfig } = originData;

      const { xName = '', xUnit = '', yNameArr = [], yUnit = '', textEllipsis, fontSize = 12 } = chartConfig;

      const defaultYInfo = {
        type: 'value',
        name: '',
        nameTextStyle: { color: this.chartTextColor, fontSize },
        axisLabel: {
          color: this.chartTextColor,
          fontSize,
          formatter: val => {
            if (yUnit) {
              val = `${val}(${yUnit})`;
            }
            return val;
          }
        }
      };
      const yAxis = yNameArr.map((yName, yIndex) => {
        let offset = 0;
        let position = 'left';
        let align = 'right';
        if (yIndex > 0) {
          position = 'right';
          align = 'left';
        }
        if (yIndex > 1) {
          offset = (yIndex - 1) * 80;
        }
        return {
          ...defaultYInfo,
          name: yName,
          position,
          offset,
          nameTextStyle: { ...defaultYInfo.nameTextStyle, align, padding: [0, 5] }
        };
      });

      if (yAxis.length < 1) {
        yAxis.push(defaultYInfo);
      }

      const barMaxWidth = 20;
      const refreshData = {
        type: this.chartType,
        isChart: true,
        config: {
          grid: {
            bottom: '40px'
          },
          legend: {
            show: legendsData.length > 1,
            type: 'scroll',
            top: 0,
            left: 0,
            itemWidth: 10,
            itemHeight: 10,
            data: legendsData
          },
          tooltip: {
            confine: true,
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            },
            formatter: val => {
              const item = val[0];
              if (isNaN(val[0].data)) {
                return item.name;
              }
              return `${item.name}：${val[0].data.toLocaleString()}`;
            }
          },
          xAxis: [
            {
              axisLabel: {
                fontSize,
                color: this.chartTextColor,
                interval: 0,
                formatter: textVal => {
                  if (xUnit) {
                    textVal = `${textVal}(${xUnit})`;
                  }
                  if (textVal.length < textEllipsis + 1) {
                    return textVal;
                  }
                  return `${textVal.slice(0, textEllipsis)}...`;
                }
              },
              type: 'category',
              data: [],
              name: xName,
              nameTextStyle: { verticalAlign: 'top', padding: [5, 0], color: this.chartTextColor, fontSize },
              axisTick: false,
              axisLine: false,
              triggerEvent: true
            }
          ],
          yAxis,
          series: []
        }
      };

      let topNum = 40;
      if (legendsData.length > 1) {
        topNum += 15;
      }
      if (this.chartTitle) {
        topNum += 15;
      }
      if (this.customLegend) {
        refreshData.config.legend.show = false;
      }

      refreshData.config.grid.top = topNum;

      if (chartConfig.left) {
        refreshData.config.grid.left = chartConfig.left;
      }
      if (chartConfig.right) {
        refreshData.config.grid.right = chartConfig.right;
      }
      if (yNameArr.length > 2) {
        if (!refreshData.config.grid.right) {
          refreshData.config.grid.right = 60;
        }
        refreshData.config.grid.right = (yNameArr.length - 2) * 80 + refreshData.config.grid.right;
      }
      if (chartConfig.bottom) {
        refreshData.config.grid.bottom = chartConfig.bottom;
      }
      if (chartConfig.rotate) {
        refreshData.config.xAxis[0].axisLabel.rotate = chartConfig.rotate;
      }

      refreshData.config.xAxis[0].data = columnsData;
      refreshData.config.series = rowsData.map(rowDataItem => {
        let yAxisIndex = yNameArr.findIndex(yName => {
          if (yName === rowDataItem.name) {
            return true;
          }
          const lastIndex = rowDataItem.name.lastIndexOf('(');
          return rowDataItem.name.slice(lastIndex + 1, -1) === yName;
        });

        if (yAxisIndex === -1) {
          yAxisIndex = 0;
        }

        return {
          type: 'bar',
          name: rowDataItem.name,
          data: rowDataItem.data,
          barMaxWidth,
          barGap: '50%',
          yAxisIndex,
          label: {
            fontSize,
            show: true,
            position: 'top',
            color: this.chartTextColor
          }
        };
      });

      if (refreshData.config.yAxis.length > 1) {
        const axisInfo = fetchAxisMaxAndInterval({
          series: refreshData.config.series,
          axisArr: refreshData.config.yAxis,
          isYAxis: true
        });
        refreshData.config.yAxis = refreshData.config.yAxis.map((axisItem, axisIndex) => {
          return { ...axisItem, ...axisInfo[axisIndex] };
        });
      }

      if (legendsData.length > 0) {
        delete refreshData.config.tooltip.formatter;
      }

      this.legendData = legendsData;

      return refreshData;
    }
  }
};
</script>

<style lang="scss" scoped>
.chart-component {
  position: relative;
  width: 100%;
  height: 100%;

  .chart-content {
    width: 100%;
    height: 100%;
  }

  .legend-content {
    box-sizing: border-box;

    .legend-item {
      display: inline-block;
      max-width: 100%;
      height: 30px;
      line-height: 30px;
      margin-right: 10px;
      .tip-content {
        display: inline-block;
        width: 10px;
        height: 10px;
        background-color: #ccc;
        margin-right: 6px;
      }
      .legend-text {
        color: #3d465a;
        font-size: 14px;
      }
    }
  }
}
</style>
